import { useEffect, useState, useRef } from "react";
import {
  Box,
  Stack,
  Button,
  ButtonGroup,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useWindowSize } from "../../../utils/useWindowSize";

import { HttpChannelApi } from "../../../interface/channel-api";
import { CommonUtils } from "../../../utils/common_utils";

import { userState } from "../../../interface/MainInterface";

import { useInterval } from "../../../utils/UseInterval";
import dayjs from "dayjs";

import Toast from "../../../utils/Toast";
import { HttpAdminApi } from "../../../interface/admin-api";

interface propsType {
  userState: userState;
  channelInfo: any;
  callback: any;
}

const channelApi = new HttpChannelApi();
const adminApi = new HttpAdminApi();
const cUtils = new CommonUtils();

const LiveStatistic = (props: propsType) => {
  const windowSize = useWindowSize();
  const toastRef: any = useRef();

  // 라이브 보드는 어드민과 슈퍼어드민만 볼 수 있다. 일반 권한 계정에게는 통계와 종료 시간 관리가 모두 보이지 않는다.
  // 슈퍼어드민이 반드시 어드민을 겸하도록 강제되어 있지 않으므로 두 값을 함께 확인한다.
  const canViewBoard = props.userState.isAdmin || props.userState.isSuperAdmin;

  const [statisticsList, setStatisticsList] = useState<any>([]);

  const [concurentView, setConcurentView] = useState(0);
  const [totalLike, setTotalLike] = useState(0);
  const [totalLiveLike, setTotalLiveLike] = useState(0);
  const [totalView, setTotalView] = useState(0);
  const [totalLiveView, setTotalLiveView] = useState(0);
  const [totalViewTime, setTotalViewTime] = useState(0);
  const [prodClickList, setProdClickList] = useState<any>([]);
  const [broadTime, setBroadTime] = useState("00:00:00");
  const [endRemainTime, setEndRemainTime] = useState("00:00:00");
  const [openEndMng, setOpenEndMng] = useState(false);
  const [showRemainTime, setShowRemainTime] = useState(false);

  useEffect(() => {
    if (canViewBoard && !cUtils.isEmptyObj(props.channelInfo)) get_statistics();
    setShowRemainTime(props.channelInfo.show_remain_time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.channelInfo, canViewBoard]);

  const get_statistics = () => {
    get_statistics_data();
    get_prod_click();
  };

  // 통계 데이터 조회
  const get_statistics_data = async () => {
    const param = {
      command: "get_statistics_data",
      pk: props.channelInfo.channel_pk,
      broad_seq: props.channelInfo.broad_seq,
      broad_status: props.channelInfo.broad_status,
    };

    const res = await channelApi.post(param);
    // console.log("statistic_data : ", res);
    if (res.result_code === "200") {
      setStatisticsList(res.result_body.statistic);
      setTotalLike(res.result_body.total_likes);
      setTotalView(res.result_body.total_views);
    }
  };

  const get_prod_click = async () => {
    const param: any = {
      command: "get_prod_click",
      broad_seq: props.channelInfo.broad_seq,
    };

    await channelApi.post(param).then((result: any) => {
      if (result.result_code === "200") {
        setProdClickList(result.result_body);
      } else {
        setProdClickList([]);
      }
    });
  };

  const handleRemainTime = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!showRemainTime && !window.confirm("잔여 시간을 표시하시겠습니까?")) return;
    if (showRemainTime && !window.confirm("잔여 시간 표시를 해제하시겠습니까?")) return;

    setShowRemainTime(e.target.checked);

    const payload = {
      command: "show_remain_time",
      channel_pk: props.channelInfo.channel_pk,
      broad_seq: props.channelInfo.broad_seq,
      flag: e.target.checked,
    };

    const res = await adminApi.post(payload);
    if (res.code !== "200") {
      toastRef.current?.toast("잔여 시간 표시/해제 실패", "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
    }
  };

  const handleExtendTime = async (minute: number) => {
    if (!window.confirm(`종료시간을 ${minute}분 연장하시겠습니까?`)) return;

    const payload = {
      command: "extend_broad_time",
      channel_pk: props.channelInfo.channel_pk,
      broad_seq: props.channelInfo.broad_seq,
      minute,
    };
    const res = await adminApi.post(payload);
    if (res.code !== "200") {
      toastRef.current?.toast("방송 시간 연장 실패", "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
    }
    props.callback({ command: "extend_broad_time" });
  };

  useEffect(() => {
    if (statisticsList.length > 0) {
      let newDataRows = [...statisticsList];

      const totalList = [];
      let lastConcurentView: any = undefined;
      let totalLiveViewTimeSec = 0;
      let totalLiveEnterCnt = 0;
      let totalLiveLikeCnt = 0;

      for (const data of newDataRows) {
        const time = dayjs(data.sk).format("YYYY-MM-DD HH:mm");
        totalList.push({
          time: time,
          concurrent: data.concurrent_cnt,
          enter: data.enter_cnt,
          like: data.like_cnt,
          diliveredTime: data.deliverd_sec,
        });
        lastConcurentView = data.concurrent_cnt;
        totalLiveViewTimeSec = totalLiveViewTimeSec + data.deliverd_sec;
        totalLiveEnterCnt = totalLiveEnterCnt + data.enter_cnt;
        totalLiveLikeCnt = totalLiveLikeCnt + data.like_cnt;
      }
      if (lastConcurentView !== undefined) {
        setConcurentView(lastConcurentView);
      }
      setTotalViewTime(totalLiveViewTimeSec);
      setTotalLiveView(totalLiveEnterCnt);
      setTotalLiveLike(totalLiveLikeCnt);
    }
  }, [statisticsList]);

  const delay: number = 60000; // 1분마다 변경
  useInterval(
    () => {
      get_statistics();
    },
    canViewBoard && props.channelInfo.broad_status === "START" ? delay : null
  );

  const time: number = 1000;
  useInterval(
    () => {
      setBroadTime(convertTotalViewTime(cUtils.calPassedSec(props.channelInfo.broad_start_tm)));
    },
    props.channelInfo.broad_status === "START" ? time : null
  );

  useInterval(
    () => {
      const remainSec = cUtils.calRemainSec(props.channelInfo.broad_stop_tm);
      setEndRemainTime(convertTotalViewTime(remainSec));
    },
    props.channelInfo.broad_status === "START" && cUtils.calRemainSec(props.channelInfo.broad_stop_tm) > 0 ? time : null
  );

  const convertTotalViewTime = (totalSec: number) => {
    const t = cUtils.convertSecToTime(totalSec);
    return `${t.hours}:${t.minutes}:${t.seconds}`;
  };

  // 훅 선언을 모두 마친 뒤에 막는다. 훅보다 앞에서 반환하면 렌더마다 훅 개수가 달라져 오류가 난다.
  if (!canViewBoard) return null;

  return (
    <>
      <Box>
        <Stack direction={"row"} spacing={2}>
          <ButtonGroup variant="outlined" fullWidth>
            {windowSize.width > 768 && (
              <Button>
                <Typography sx={{ fontSize: "20px", fontWeight: "700", color: "#000000" }}>라이브 보드</Typography>
              </Button>
            )}
            <Button
              onClick={() => {
                // setViewChart(1);
              }}
            >
              <Stack direction={"column"} spacing={0}>
                <Typography
                  sx={{ fontSize: windowSize.width > 768 ? "20px" : "14px", fontWeight: "700", color: "#000000" }}
                >
                  {windowSize.width > 768
                    ? `${cUtils.numericComma(totalLiveView)}명`
                    : `${cUtils.numericComma(totalLiveView)}`}
                </Typography>
                <Typography
                  sx={{ fontSize: windowSize.width > 768 ? "12px" : "10px", fontWeight: "400", color: "#00000090" }}
                >
                  {windowSize.width > 768
                    ? `누적 시청자수 (VOD:${cUtils.numericComma(totalView - totalLiveView)}명)`
                    : `누적(V:${cUtils.numericComma(totalView - totalLiveView)})`}
                </Typography>
              </Stack>
            </Button>
            <Button
              onClick={() => {
                // setViewChart(0);
              }}
            >
              <Stack direction={"column"} spacing={0}>
                <Typography
                  sx={{ fontSize: windowSize.width > 768 ? "20px" : "14px", fontWeight: "700", color: "#000000" }}
                >
                  {windowSize.width > 768
                    ? `${cUtils.numericComma(concurentView)}명`
                    : `${cUtils.numericComma(concurentView)}`}
                </Typography>
                <Typography
                  sx={{ fontSize: windowSize.width > 768 ? "12px" : "10px", fontWeight: "400", color: "#00000090" }}
                >
                  {windowSize.width > 768 ? "동시 시청자수" : "동시"}
                </Typography>
              </Stack>
            </Button>
            {/* <Button
              onClick={() => {
                // setViewChart(1);
              }}
            >
              <Stack direction={"column"} spacing={0}>
                <Typography
                  sx={{ fontSize: windowSize.width > 768 ? "20px" : "14px", fontWeight: "700", color: "#000000" }}
                >
                  {windowSize.width > 768
                    ? `${cUtils.numericComma(totalLiveLike)}건`
                    : `${cUtils.numericComma(totalLiveLike)}`}
                </Typography>
                <Typography
                  sx={{ fontSize: windowSize.width > 768 ? "12px" : "10px", fontWeight: "400", color: "#00000090" }}
                >
                  {windowSize.width > 768
                    ? `좋아요수 (VOD:${cUtils.numericComma(totalLike - totalLiveLike)}명)`
                    : `좋아요(V:${cUtils.numericComma(totalLike - totalLiveLike)})`}
                </Typography>
              </Stack>
            </Button> */}
            <Button
              onClick={() => {
                // setViewChart(3);
              }}
            >
              <Stack direction={"column"} spacing={0}>
                <Typography
                  sx={{ fontSize: windowSize.width > 768 ? "20px" : "14px", fontWeight: "700", color: "#000000" }}
                >
                  {windowSize.width > 768
                    ? `${cUtils.numericComma(prodClickList.length)}회`
                    : `${cUtils.numericComma(prodClickList.length)}`}
                </Typography>
                <Typography
                  sx={{ fontSize: windowSize.width > 768 ? "12px" : "10px", fontWeight: "400", color: "#00000090" }}
                >
                  {windowSize.width > 768 ? "상품 클릭수" : "상품"}
                </Typography>
              </Stack>
            </Button>
            {props.channelInfo.broad_status === "START" && windowSize.width > 768 && (
              <>
                <Button
                  onClick={() => {
                    // setViewChart(3);
                  }}
                >
                  <Stack direction={"column"} spacing={0}>
                    <Typography sx={{ fontSize: "20px", fontWeight: "700", color: "#000000" }}>{broadTime}</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "400", color: "#00000090" }}>
                      라이브 진행시간
                    </Typography>
                  </Stack>
                </Button>
                <Button
                  onClick={() => {
                    setOpenEndMng(true);
                  }}
                >
                  <Stack direction={"column"} spacing={0}>
                    <Typography sx={{ fontSize: "20px", fontWeight: "700", color: "#000000" }}>
                      {endRemainTime}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: "400", color: "#00000090" }}>
                      종료 예정 시간 (종료 관리)
                    </Typography>
                  </Stack>
                </Button>
              </>
            )}
          </ButtonGroup>
        </Stack>
      </Box>
      <Toast ref={toastRef} />
      <Dialog
        open={openEndMng}
        onClose={() => {
          setOpenEndMng(false);
        }}
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "56.25vh" } } }}
      >
        <DialogTitle>종료 시간 관리</DialogTitle>
        <DialogContent>
          <Stack spacing={1} useFlexGap>
            <Stack direction="row" spacing={6} useFlexGap>
              <Stack spacing={0.5} useFlexGap>
                <Typography variant="body1" sx={{ color: "#FF3741" }}>
                  [방송 예정 종료 시간]
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {dayjs(props.channelInfo.broad_stop_tm).format("MM월 DD일 HH시 mm분 ss초")}
                </Typography>
              </Stack>
              <Stack spacing={0.5} useFlexGap>
                <Typography variant="body1" sx={{ color: "#FF3741" }}>
                  [남은 시간]
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {endRemainTime}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={0} useFlexGap sx={{ alignItems: "center" }}>
              <FormControlLabel
                label="※ 잔여 시간 노출 여부"
                labelPlacement="start"
                control={<Checkbox checked={showRemainTime} onChange={handleRemainTime} />}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: "red",
                    fontWeight: 700,
                  },
                  m: 0,
                  gap: 0,
                }}
              />
              <Typography sx={{ fontWeight: 500, color: "blue", fontSize: "14px" }}>
                선택 시 남은 시간이 화면에 표시됩니다.
              </Typography>
            </Stack>
            <Stack spacing={0.5} useFlexGap>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#1876d2" }}>
                종료 시간 연장
              </Typography>
              <ButtonGroup variant="outlined">
                <Button onClick={() => handleExtendTime(1)}>1분</Button>
                <Button onClick={() => handleExtendTime(5)}>5분</Button>
                <Button onClick={() => handleExtendTime(10)}>10분</Button>
                <Button onClick={() => handleExtendTime(15)}>15분</Button>
                <Button onClick={() => handleExtendTime(30)}>30분</Button>
                <Button onClick={() => handleExtendTime(60)}>1시간</Button>
              </ButtonGroup>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setOpenEndMng(false);
            }}
          >
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LiveStatistic;
